import { 
  Subscription, 
  SubscriptionPlan, 
  BillingHistoryItem, 
  FeatureAccess,
  CreateSubscriptionRequest,
  UpdateSubscriptionRequest,
  CancelSubscriptionRequest,
  FeatureType
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

let unauthorizedCallback: (() => void) | null = null;

export const setSubscriptionApiUnauthorizedCallback = (callback: () => void) => {
  unauthorizedCallback = callback;
};

const getAuthToken = (): string | null => {
  const persistedState = localStorage.getItem('auth-store');
  if (persistedState) {
    try {
      const parsedState = JSON.parse(persistedState);
      return parsedState.state?.token || null;
    } catch (error) {
      console.error('Error parsing auth state:', error);
      return null;
    }
  }
  return null;
};

const makeAuthenticatedRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const token = getAuthToken();
  
  const config: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

  if (response.status === 401) {
    if (unauthorizedCallback) {
      unauthorizedCallback();
    }
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // If JSON parsing fails, use the status text
      errorMessage = response.statusText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

const makePublicRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const config: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

export const subscriptionApi = {
  // Get current user's subscription
  getCurrentSubscription: async (): Promise<Subscription | null> => {
    return await makeAuthenticatedRequest<Subscription | null>('/subscription');
  },

  // Create Stripe checkout session
  createCheckoutSession: async (request: CreateSubscriptionRequest): Promise<{ checkoutUrl: string; sessionId: string }> => {
    return makeAuthenticatedRequest<{ checkoutUrl: string; sessionId: string }>('/subscription/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  // Handle checkout success
  handleCheckoutSuccess: async (sessionId: string): Promise<{ success: boolean; message: string; subscription?: Subscription }> => {
    return makePublicRequest<{ success: boolean; message: string; subscription?: Subscription }>(`/subscription/success?session_id=${sessionId}`);
  },

  // Update existing subscription
  updateSubscription: async (request: UpdateSubscriptionRequest): Promise<Subscription> => {
    return makeAuthenticatedRequest<Subscription>('/subscription/update', {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  },

  // Cancel subscription
  cancelSubscription: async (request: CancelSubscriptionRequest): Promise<Subscription> => {
    return makeAuthenticatedRequest<Subscription>('/subscription/cancel', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  // Get billing history
  getBillingHistory: async (): Promise<BillingHistoryItem[]> => {
    return makeAuthenticatedRequest<BillingHistoryItem[]>('/subscription/billing-history');
  },

  // Create customer portal session
  createCustomerPortalSession: async (): Promise<{ url: string }> => {
    return makeAuthenticatedRequest<{ url: string }>('/subscription/customer-portal', {
      method: 'POST',
    });
  },

  // Get feature access
  getFeatureAccess: async (): Promise<FeatureAccess> => {
    return makeAuthenticatedRequest<FeatureAccess>('/subscription/feature-access');
  },

  // Check if user can access specific feature
  canAccessFeature: async (feature: FeatureType): Promise<boolean> => {
    const featureAccess = await subscriptionApi.getFeatureAccess();
    
    switch (feature) {
      case FeatureType.BasicHabits:
        return true; // Always available
      case FeatureType.Analytics:
        return featureAccess.canAccessAnalytics;
      case FeatureType.Export:
        return featureAccess.canExportData;
      case FeatureType.PrioritySupport:
        return featureAccess.hasPrioritySupport;
      default:
        return false;
    }
  },

  // Get available subscription plans (public endpoint)
  getSubscriptionPlans: async (): Promise<SubscriptionPlan[]> => {
    const response = await makePublicRequest<{ plans: SubscriptionPlan[] }>('/subscription/plans');
    return response.plans;
  },

  // Initialize grace period for existing users (admin only)
  initializeGracePeriod: async (): Promise<void> => {
    await makeAuthenticatedRequest<void>('/subscription/initialize-grace-period', {
      method: 'POST',
    });
  },
}; 